import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useTranslation } from 'react-i18next';
import type { PublicSeoResult } from '../../api/_lib/publicSeo';

interface SEOProps {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    lang?: string;
    type?: string;
    noindex?: boolean;
    /**
     * Public restaurant pages only: tags computed by the shared helper (api/_lib/publicSeo),
     * the same values api/render.ts writes into the initial HTML. When present, they replace
     * every other prop so server and client tags are identical (no conflicting canonical/robots).
     */
    publicSeo?: PublicSeoResult;
}

const OG_LOCALES: Record<string, string> = { it: 'it_IT', en: 'en_US', es: 'es_ES', fr: 'fr_FR', de: 'de_DE' };

function publicSeoTags(seo: PublicSeoResult): React.ReactElement[] {
    const tags: React.ReactElement[] = [
        <html key="html" lang={seo.htmlLang} />,
        <title key="title">{seo.title}</title>,
        <meta key="robots" name="robots" content={seo.robots} />,
        <link key="canonical" rel="canonical" href={seo.canonical} />,
        ...seo.alternates.map((a) => <link key={`hreflang-${a.hreflang}`} rel="alternate" hrefLang={a.hreflang} href={a.href} />),
        <meta key="og:type" property="og:type" content="website" />,
        <meta key="og:site_name" property="og:site_name" content="Deskbay" />,
        <meta key="og:url" property="og:url" content={seo.canonical} />,
        <meta key="og:title" property="og:title" content={seo.title} />,
        <meta key="og:image" property="og:image" content={seo.image} />,
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />,
        <meta key="twitter:title" name="twitter:title" content={seo.title} />,
        <meta key="twitter:image" name="twitter:image" content={seo.image} />,
    ];
    if (OG_LOCALES[seo.htmlLang]) {
        tags.push(<meta key="og:locale" property="og:locale" content={OG_LOCALES[seo.htmlLang]} />);
    }
    if (seo.description) {
        tags.push(
            <meta key="description" name="description" content={seo.description} />,
            <meta key="og:description" property="og:description" content={seo.description} />,
            <meta key="twitter:description" name="twitter:description" content={seo.description} />,
        );
    }
    return tags;
}

export const SEO: React.FC<SEOProps> = ({
    title,
    description,
    image = 'https://app.deskbay.io/deskbay-logo-full.png',
    url,
    lang,
    type = 'website',
    noindex = true, // User requested noindex for now
    publicSeo,
}) => {
    const { i18n } = useTranslation();
    if (publicSeo) {
        return <Helmet>{publicSeoTags(publicSeo)}</Helmet>;
    }
    const currentLang = lang || i18n.language || 'it';
    const siteName = 'Deskbay';
    const fullTitle = title ? `${title} | ${siteName}` : siteName;
    const currentUrl = url || window.location.href;

    // Build hreflang URLs
    const baseUrl = currentUrl.split('?')[0];
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const view = urlParams.get('view');

    const buildHrefLangUrl = (langCode: string) => {
        const params = new URLSearchParams();
        if (view) params.set('view', view);
        params.set('lang', langCode);
        return `${baseUrl}?${params.toString()}`;
    };

    const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';
    const desc = description || '';

    // @dr.pogodin/react-helmet v3 is extremely strict:
    // - No JSX comments inside <Helmet>
    // - No conditional renders ({x && <tag/>} produces false which is invalid)
    // - No whitespace-only text nodes
    // Build children array explicitly to avoid any invalid descendants
    const helmetChildren: React.ReactElement[] = [
        <html key="html" lang={currentLang} />,
        <title key="title">{fullTitle}</title>,
        <meta key="robots" name="robots" content={robotsContent} />,
        <link key="canonical" rel="canonical" href={currentUrl} />,
        <link key="hreflang-it" rel="alternate" hrefLang="it" href={buildHrefLangUrl('it')} />,
        <link key="hreflang-en" rel="alternate" hrefLang="en" href={buildHrefLangUrl('en')} />,
        <link key="hreflang-es" rel="alternate" hrefLang="es" href={buildHrefLangUrl('es')} />,
        <link key="hreflang-fr" rel="alternate" hrefLang="fr" href={buildHrefLangUrl('fr')} />,
        <link key="hreflang-default" rel="alternate" hrefLang="x-default" href={baseUrl} />,
        <meta key="og:type" property="og:type" content={type} />,
        <meta key="og:title" property="og:title" content={fullTitle} />,
        <meta key="og:image" property="og:image" content={image} />,
        <meta key="og:url" property="og:url" content={currentUrl} />,
        <meta key="og:site_name" property="og:site_name" content={siteName} />,
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />,
        <meta key="twitter:title" name="twitter:title" content={fullTitle} />,
        <meta key="twitter:image" name="twitter:image" content={image} />,
    ];

    if (desc) {
        helmetChildren.push(
            <meta key="description" name="description" content={desc} />,
            <meta key="og:description" property="og:description" content={desc} />,
            <meta key="twitter:description" name="twitter:description" content={desc} />,
        );
    }

    return <Helmet>{helmetChildren}</Helmet>;
};
